import { ComponentBase } from './ComponentBase';
import { template, unsafeHTML } from '../utils/TemplateParser';
import { lang } from '../lang';
import { formatStartDate } from '../utils';
import placeholderImage from '../../images/event-placeholder.png';

export class FeaturedSlider extends ComponentBase {
    constructor(options = {}) {
        super();
        this.featuredEventsBaseIndex = 0;
        this.defaultLayout = options.defaultLayout;
    }

    registerEvents() {
        return {
            'click .featured-events-previous a' : 'previousFeaturedEvents',
            'click .featured-events-next a'     : 'nextFeaturedEvents',
            'click a[data-action="routeChange"]': 'handleRouteChange',
            'keydown .featured-event-wrapper'   : 'keyboard',
        };
    }

    render(){
        this.#setupEventsPerSlide();
        super.render();
    }

    template() {
        const featuredEvents = this.featuredEventsCollection;
        if (featuredEvents.length < 2) return ''; // Don't display slider if there are fewer than 2 events.
        
        // No need to display previous/next controls when featured events are equal to the slider's capacity.
        const controls = `
            <div class="featured-events-previous ${featuredEvents.length == this.eventsPerSlide ? 'hide' : ''}">
                <a href="javascript:;" title="${lang.Previous}" aria-label="${lang.Previous}"><i class="icon fas fa-chevron-left"></i></a>
            </div>
            <div class="featured-events-next ${featuredEvents.length == this.eventsPerSlide ? 'hide' : ''}">
                <a href="javascript:;" title="${lang.Next}" aria-label="${lang.Next}"><i class="icon fas fa-chevron-right"></i></a>
            </div>
        `;

        const featuredEventTemplates = this.#getNextTemplates();

        return template`
            <div class="featured-event-outer hide">
                <h3>${lang.FeaturedEvents}</h3>
                    <div class="featured-event-wrapper">
                    ${unsafeHTML(controls)}
                    <ul class="featured-events ${this.eventsPerSlide < 5 ? `capacity-${this.eventsPerSlide}` : ''}">
                        ${unsafeHTML(featuredEventTemplates)}
                    </ul>
                </div>
            </div>`;
    }

    domReady() {
        this.elements = {
            outer: this.el.querySelector('.featured-event-outer'),
            wrapper: this.el.querySelector('.featured-event-wrapper'),
            slider: this.el.querySelector('.featured-events'),
            previousButton: this.el.querySelector('.featured-events-previous'),
            nextButton: this.el.querySelector('.featured-events-next'),
        };
    }

    show() {
        this.elements?.outer?.classList.remove('hide');
    }

    hide() {
        this.elements?.outer?.classList.add('hide');
    }

    /* Event Handlers */
    previousFeaturedEvents() {
        const {wrapper, slider} = this.elements;
        const width = wrapper.offsetWidth;
        const left = slider.offsetLeft;
        const html = this.#getPreviousTemplates();

        slider.insertAdjacentHTML('afterbegin', html);
        slider.style.left = `${left-width}px`;
        
        setTimeout(() => {
            slider.classList.add('animate');
            slider.style.left = '0px';
            this.elements.previousButton.querySelector('a').focus();
        }, 1);

        setTimeout(() => {
            slider.classList.remove('animate');
            slider.querySelectorAll(`li:nth-last-child(-n+${this.eventsPerSlide})`).forEach(e => e.remove()); // removes last n li tags which are now out of view
        }, 500);
    }

    nextFeaturedEvents() {
        const {wrapper, slider} = this.elements;
        const width = wrapper.offsetWidth;
        const left = slider.offsetLeft;
        const html = this.#getNextTemplates();

        slider.insertAdjacentHTML('beforeend', html);
        slider.classList.add('animate');
        slider.style.left = `${left-width}px`;
        this.elements.nextButton.querySelector('a').focus();

        setTimeout(() => {
            slider.classList.remove('animate');
            slider.querySelectorAll(`li:nth-child(-n+${this.eventsPerSlide})`).forEach(e => e.remove()); // removes first n li tags which are now out of view
            slider.style.left = '0';
        }, 500);
    }

    keyboard(e) {
        if(e.target.closest('.featured-event')) {
            this.keyboardSliderLinks(e);
        } else {
            this.keyboardArrowButtons(e);
        }
    }

    keyboardArrowButtons(e) {
        switch (e.key) {
            case "ArrowLeft":
                this.previousFeaturedEvents();
                break;
            case "ArrowRight":
                this.nextFeaturedEvents();
                break;
            default:
                return;
        }
        e.preventDefault();
    }

    keyboardSliderLinks(e) {
        const listItem = e.target.closest('li');
        const listItems = Array.from(this.elements.slider.children);

        const focusPrevious = () => {
            const previousIndex = listItems.indexOf(listItem) - 1;
            if(previousIndex < 0) return false;
            listItems[previousIndex].querySelector('a').focus();
            return true;
        }
        const focusNext = () => {
            const nextIndex = listItems.indexOf(listItem) + 1;
            if(nextIndex >= this.eventsPerSlide) return false;
            listItems[nextIndex].querySelector('a').focus();
            return true;
        }

        switch (e.key) {
            case "ArrowLeft":
                if(!focusPrevious()) this.previousFeaturedEvents();
                break;
            case "ArrowRight":
                if(!focusNext()) this.nextFeaturedEvents();
                break;
            default:
                return;
        }
        e.preventDefault();
    }

    /* Private Methods */
    #setupEventsPerSlide() {
        // Determines how many events will be displayed at a time based on screen width and total number of events
        const eventCount = this.featuredEventsCollection.length;
        const minEventsPerSlide = 2;
        const maxEventsPerSlide = eventCount < 5 ? eventCount : 5;
        const mql = window.matchMedia('(max-width: 60em)'); // set MediaQueryList
        this.eventsPerSlide = mql.matches ? minEventsPerSlide : maxEventsPerSlide; // set initial events per slide
        mql.addEventListener('change', e => e.matches ? this.#resetSlider(minEventsPerSlide) : this.#resetSlider(maxEventsPerSlide), {signal: this.eventController.signal });
    }

    #resetSlider(eventsPerSlide) {
        // Resets slider based on new number of events per slide
        const slider = this.elements.slider;
        if(!slider) return; // if no events just return

        slider.setAttribute('class', `featured-events capacity-${eventsPerSlide}`);
        this.#displayControls(this.featuredEventsCollection.length != eventsPerSlide);
        this.featuredEventsBaseIndex = 0;
        this.eventsPerSlide = eventsPerSlide;
        slider.innerHTML = this.#getNextTemplates();
    }

    #displayControls(show) {
        const {previousButton, nextButton} = this.elements;

        if(show){
            previousButton.classList.remove('hide');
            nextButton.classList.remove('hide');
        } else {
            previousButton.classList.add('hide');
            nextButton.classList.add('hide');
        }
    }

    #getPreviousTemplates() {
        this.featuredEventsBaseIndex -= this.eventsPerSlide;
        const events = [];
        for (let i = 1; i <= this.eventsPerSlide; i++) {
            events.unshift(this.#eventTemplate(this.#getEvent(this.featuredEventsBaseIndex - i)));
        }
        return events.map(i => i[0].outerHTML).join('');
    }

    #getNextTemplates() {
        const events = [];
        for (let i = 0; i < this.eventsPerSlide; i++) {
            events.push(this.#eventTemplate(this.#getEvent(this.featuredEventsBaseIndex + i)));
        }
        this.featuredEventsBaseIndex += this.eventsPerSlide;
        return events.map(i => i[0].outerHTML).join('');
    }

    #eventTemplate(event) {
        const image = event.image ? event.image : placeholderImage;
        const imageAltText = event.image ? event.imageAltText : lang.PlaceholderImage;
        let href = this.getRouteURL(`event-details/${event.id}`);
        if(event.recurring) href += `/instances/${event.startDate ?? event.startDatetime}`;
        return template`
            <li data-name="${event.title}">
                <a href="${href}" class="featured-event" data-action="routeChange" aria-label="${event.title}">
                    <div class="featured-event-image" style="background-image: url(${image})" aria-label="${imageAltText}">
                    </div>
                    <div class="featured-event-content">
                        <h3 class="featured-event-title">${event.title}</h3>
                        <div class="date" aria-label="${lang.EventDate}">${unsafeHTML(formatStartDate(event, true))}</div>
                    </div>
                </a>
            </li>
        `;
    }

    #getEvent(cyclicalIndex) {
        // cyclicalIndex is the position that the slider events has incremented to which can be outside the range of the array.
        const featuredEvents = this.featuredEventsCollection;
        const index = cyclicalIndex % featuredEvents.length; // Translates cyclicalIndex to the correct index within the range of the array.
        return featuredEvents.slice(index)[0]; // Utilizing slice to support both positive and negative index values.
    }
}